import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import '../../../i18n'
import Checkbox from '../../UI/Checkbox/Checkbox';
import ErrorBox from '../../UI/ErrorBox/ErrorBox';
import ReactHtmlParser from 'html-react-parser';

// PADD-3796: resolve the single renewal-terms string to display.
//
// Renewal terms are resolved server-side (BasketProcessor) to a single basket-level pair:
//   override  — the first non-blank per-variant override across the basket (applied-discount
//               override wins over variant override). Empty => use the global brand asset.
//   mediaType — "print" | "onlinedigital" | "both" to pick the global asset, or "notapplicable"
//               when the basket has no continuous/recurring payment method (nothing to show).
//
// Text precedence (highest to lowest):
//   1. Discount override  — DISCOUNT_RENEWAL_TERMS_OVERRIDE (resolved server-side)
//   2. Variant override   — RENEWAL_TERMS_OVERRIDE (resolved server-side)
//   3. Global asset        — brand-level asset for the basket media type (print / onlinedigital / both)
// Returns a single HTML string (or '' when there is nothing to show).
export function buildRenewalTerms(override, mediaType, globalAssets) {
    if (mediaType === 'notapplicable') {
        return '';
    }

    if (override && override.trim() !== '') {
        return override;
    }

    // No override — use the global asset for the basket's combined media type.
    if (mediaType === 'print') return globalAssets?.print ?? globalAssets?.both ?? '';
    if (mediaType === 'onlinedigital') return globalAssets?.onlineDigital ?? globalAssets?.both ?? '';
    return globalAssets?.both ?? '';
}

export function AutorenewalTerms(props) {
    const { t, i18n } = useTranslation();

    // Default to true when the prop is not supplied so existing behaviour (checkbox shown) is preserved.
    const showConsentCheckbox = props.showConsentCheckbox !== false;

    const termsHtml = useMemo(
        () => buildRenewalTerms(props?.override, props?.mediaType, props?.globalAssets),
        [props?.override, props?.mediaType, props?.globalAssets, i18n.language]
    );

    // PADD-3796: a single consent checkbox gates submission for the renewal terms.
    const [isChecked, setIsChecked] = useState(false);

    useEffect(() => {
        // When the consent checkbox is hidden via config, treat consent as implicitly given and clear
        // any pending validation error so submission is never blocked.
        if (!showConsentCheckbox) {
            props.setIsAutoRenewalChecked(true);
            props.setErrorStates(errorStates => ({ ...errorStates, autoRenewalNotTicked: false }));
            return;
        }

        props.setIsAutoRenewalChecked(isChecked);
        if (isChecked) {
            props.setErrorStates(errorStates => ({ ...errorStates, autoRenewalNotTicked: false }));
        }
    }, [isChecked, showConsentCheckbox]);

    const toggle = () => setIsChecked(prev => !prev);

    return (
        <div className="item-container">
            <label className="label">{t('autorenewalterms.title')}</label>
            <div className="item-wrapper">
                {termsHtml ? <div className="renewal-terms-block">{ReactHtmlParser(termsHtml)}</div> : null}
                {showConsentCheckbox &&
                    <>
                        <Checkbox
                            label={t('autorenewalterms.consentcheckboxlabel')}
                            checked={isChecked}
                            onChange={toggle}
                            id="autorenewalconsent"
                            name="autorenewalconsent" />
                        {props.errorStates?.autoRenewalNotTicked && !isChecked ?
                            <span role="alert" id="payment-not-selected"><ErrorBox>{t('payment.autorenewalcheckboxnotticked')}</ErrorBox></span>
                            : ""}
                    </>
                }
            </div>
        </div>
    );
}
